import { isHoldEligibleDuffelOffer } from "../../../unifiedFlightSearch/flightHoldEligibility";
import { isUnifiedDuffelOffer } from "../../../unifiedFlightSearch/unifiedFlightBookingGuard";

/**
 * Per-offer capabilities only. No evidence → omit (not invented true).
 * @param {object|null|undefined} offer
 * @param {{ pricing?: object|null, hasPaymentOrder?: boolean }} [ctx]
 * @returns {import("../types").OfferCapabilitiesViewModel}
 */
export function mapOfferCapabilitiesViewModel(offer, ctx = {}) {
  /** @type {import("../types").OfferCapabilitiesViewModel} */
  const caps = {};

  if (isHoldEligibleDuffelOffer(offer)) {
    caps.canHold = true;
  } else if (isUnifiedDuffelOffer(offer)) {
    caps.canHold = false;
  }

  const book = deriveCanBook(offer);
  if (book !== undefined) caps.canBook = book;

  if (ctx.hasPaymentOrder === true) {
    caps.canPay = true;
  } else if (
    offer?.paymentRequirements &&
    typeof offer.paymentRequirements.requiresInstantPayment === "boolean"
  ) {
    // Evidence that payment rules are known for this offer — not a free canPay.
    caps.canPay = offer.paymentRequirements.requiresInstantPayment === true;
  }

  if (hasBaggagePurchaseOptions(offer)) {
    caps.canAddBaggage = true;
  } else if (offer?.availableExtras != null || offer?.rawDeal?.supplement != null) {
    caps.canAddBaggage = false;
  }

  if (offer?.supportsMultipleFares === true) {
    caps.supportsMultipleFares = true;
  } else if (Array.isArray(offer?.fareFamilies) && offer.fareFamilies.length > 1) {
    caps.supportsMultipleFares = true;
  }

  const pr = offer?.passengerRequirements;
  if (pr && typeof pr.identityDocumentsRequired === "boolean") {
    caps.requiresPassengerPassport = pr.identityDocumentsRequired === true;
  }

  const reprice = deriveRequiresReprice(offer);
  if (reprice !== undefined) caps.requiresReprice = reprice;

  if (offer?.supportsSplitPayment === true) {
    caps.supportsSplitPayment = true;
  } else if (offer?.paymentRequirements?.supportsSplitPayment === true) {
    caps.supportsSplitPayment = true;
  }

  return caps;
}

function deriveCanBook(offer) {
  if (!offer || typeof offer !== "object") return undefined;

  if (isUnifiedDuffelOffer(offer)) {
    if (offer.duffelResolved !== true) return false;
    const mode = String(offer.bookingMode || "").toLowerCase();
    if (mode === "instant" || mode === "hold-eligible" || mode === "hold") {
      return true;
    }
    // Resolved but mode unknown — do not invent true
    return undefined;
  }

  // LDS / legacy adapted deal: per-offer inventory unit with id + rawDeal
  if (offer.rawDeal && offer.id) return true;

  return undefined;
}

function deriveRequiresReprice(offer) {
  if (!isUnifiedDuffelOffer(offer)) return undefined;
  if (offer.duffelResolved === true) return false;
  if (offer.duffelResolved === false) return true;
  // Unified Duffel without resolve flag — treat as requiring resolve
  return true;
}

function hasBaggagePurchaseOptions(offer) {
  const extras = Array.isArray(offer?.availableExtras) ? offer.availableExtras : [];
  if (
    extras.some((e) => {
      const t = String(e?.type || "").toLowerCase();
      return t === "baggage" || e?.metadata?.baggageType || e?.baggageType;
    })
  ) {
    return true;
  }
  const supp = offer?.rawDeal?.supplement;
  if (supp == null) return false;
  const list = Array.isArray(supp) ? supp : [supp];
  return list.length > 0;
}
